import React, { useState, useCallback, ChangeEvent, useMemo } from 'react';
import {
	Link as MuiLink,
	Container,
	Grid,
	CssBaseline,
	Typography,
	TextField,
	Button,
	Paper,
	FormControlLabel,
	Checkbox,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { Link } from 'react-router-dom';
import { useRecoilValue, useRecoilState } from 'recoil';
import { configState } from '../../features/config';
import { useTranslation } from 'react-i18next';
import { useRedirectBack, useSignUp, persistState } from '../../features/auth';
import Alert from '@material-ui/lab/Alert';

const useStyles = makeStyles((theme) => ({
	paper: {
		alignSelf: 'center',
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'center',
		padding: theme.spacing(2),
	},
	icon: {
		width: 'auto',
		height: 128,
	},
	form: {
		width: '100%', // Fix IE 11 issue.
		marginTop: theme.spacing(1),
	},
	submit: {
		margin: theme.spacing(3, 0, 2),
	},
}));

const useLabelStyles = makeStyles((theme) => ({
	label: {
		userSelect: 'none',
	},
}));

export default function SignUp() {
	const classes = useStyles();
	const labelClasses = useLabelStyles();
	const [username, setUsername] = useState('');
	const [password, setPassword] = useState('');
	const [email, setEmail] = useState('');
	const [persist, setPersist] = useRecoilState(persistState);

	useRedirectBack();

	const handleTogglePersist = useCallback(() => {
		setPersist((persist) => {
			localStorage.setItem('persist', (!persist).toString());
			return !persist;
		});
	}, [setPersist]);

	const { t } = useTranslation(['translation']);

	const config = useRecoilValue(configState);
	const { PORTAL_ICON } = config;

	const { handleSignUp, error } = useSignUp();

	const handleSignUpButtonClick = useCallback(() => {
		handleSignUp(username, password, email, persist);
	}, [username, password, email, persist, handleSignUp]);

	const handleUsernameInputChange = useCallback(
		(e: ChangeEvent<HTMLInputElement>) => {
			setUsername(e.target.value);
		},
		[]
	);
	const handlePasswordInputChange = useCallback(
		(e: ChangeEvent<HTMLInputElement>) => {
			setPassword(e.target.value);
		},
		[]
	);
	const handleEmailInputChange = useCallback(
		(e: ChangeEvent<HTMLInputElement>) => {
			setEmail(e.target.value);
		},
		[]
	);
	return useMemo(
		() => (
			<Container component="main" maxWidth="xs">
				<CssBaseline />
				<Paper className={classes.paper} elevation={6}>
					<Grid container alignItems="center" justify="center">
						<img
							alt="icon"
							src={PORTAL_ICON}
							className={classes.icon}
						/>
					</Grid>
					<Typography component="h1" variant="h5" align="center">
						{t('signUpForm.title')}
					</Typography>
					<form className={classes.form} noValidate>
						<TextField
							variant="outlined"
							margin="normal"
							required
							fullWidth
							id="username"
							label={t('signUpForm.username')}
							name="username"
							autoFocus
							value={username}
							onChange={handleUsernameInputChange}
						/>
						<TextField
							variant="outlined"
							margin="normal"
							required
							fullWidth
							name="password"
							label={t('signUpForm.password')}
							type="password"
							id="password"
							autoComplete="current-password"
							value={password}
							onChange={handlePasswordInputChange}
						/>
						<TextField
							variant="outlined"
							margin="normal"
							fullWidth
							name="email"
							label={t('signUpForm.email')}
							type="email"
							id="email"
							value={email}
							onChange={handleEmailInputChange}
						/>
						<FormControlLabel
							classes={labelClasses}
							control={
								<Checkbox
									value="remember"
									color="primary"
									checked={persist}
									onChange={handleTogglePersist}
								/>
							}
							label={t('signInForm.staySignedIn')}
						/>
						{error ? (
							<Alert severity="error">
								{t('signUpForm.usernameOrEmailTaken')}
							</Alert>
						) : null}
						<Button
							fullWidth
							variant="contained"
							color="primary"
							className={classes.submit}
							onClick={handleSignUpButtonClick}
						>
							{t('signUpForm.submit')}
						</Button>
						<Grid container justify="flex-end">
							<Grid item>
								<Link to="/sign_in">
									<MuiLink component="span" variant="body2">
										{t('signUpForm.signIn')}
									</MuiLink>
								</Link>
							</Grid>
						</Grid>
					</form>
				</Paper>
			</Container>
		),
		[
			t,
			classes.icon,
			classes.form,
			classes.paper,
			classes.submit,
			labelClasses,
			PORTAL_ICON,
			username,
			password,
			email,
			error,
			persist,
			handleUsernameInputChange,
			handlePasswordInputChange,
			handleEmailInputChange,
			handleSignUpButtonClick,
			handleTogglePersist,
		]
	);
}
